/**
 * Messenger Intercom (chatbot d'aide).
 *
 * L'App ID n'est pas un secret : il part en clair dans le bundle, comme sur tout site utilisant
 * Intercom. Il vit donc en constante, au même titre que l'URL du centre d'aide de la Topbar.
 *
 * L'identité de l'utilisateur n'est jamais transmise en clair : elle est signée par le back
 * (`GET /auth/intercom/messenger-token`) et voyage dans le JWT. Un attribut envoyé en clair à
 * côté du jeton resterait modifiable depuis le navigateur, ce qu'Intercom signale comme
 * « attribut non protégé ».
 */

export const INTERCOM_APP_ID = 'qd4ss0dk';

/** Marge avant expiration : le jeton est renouvelé pendant qu'il est encore valide. */
export const INTERCOM_TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

/** Plancher de reprogrammation, pour ne pas boucler sur le back si l'horloge dérive. */
export const INTERCOM_TOKEN_MIN_REFRESH_MS = 60 * 1000;

export type IntercomEnv = {
  isBuiltBundle: boolean;
  forceEnabled?: string;
};

/**
 * Le Messenger ne tourne que sur un bundle construit, donc staging et production : chaque boot
 * crée un contact côté Intercom, et les sessions de dev pollueraient la boîte de réception du
 * support. On se cale sur la sortie du build plutôt que sur la valeur de `VITE_ENVIRONMENT`, qui
 * vit dans les secrets de déploiement et n'est donc pas vérifiable depuis le repo.
 * `VITE_INTERCOM_ENABLED=true` dans un `.env.local` rouvre le widget pour tester en local.
 */
export function isIntercomEnabled(env: IntercomEnv): boolean {
  if (env.forceEnabled === 'true') return true;
  return env.isBuiltBundle;
}

/**
 * Délai avant de redemander un jeton. Le back en émet un valable une heure, alors qu'une session
 * Welya vit bien plus longtemps : sans renouvellement, un onglet resté ouvert perdrait son
 * identité signée. Renvoie `null` si l'échéance est inexploitable, auquel cas on s'en tient au
 * jeton courant plutôt que de programmer un rafraîchissement à l'aveugle.
 */
export function intercomTokenRefreshDelayMs(
  expiresAt: string | null | undefined,
  now: number,
): number | null {
  if (expiresAt == null || expiresAt === '') return null;

  const expiry = Date.parse(expiresAt);
  if (Number.isNaN(expiry)) return null;

  const delay = expiry - INTERCOM_TOKEN_REFRESH_MARGIN_MS - now;
  return Math.max(delay, INTERCOM_TOKEN_MIN_REFRESH_MS);
}

export type IntercomProfile = {
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
};

/**
 * Champs du profil recopiés par le back dans le jeton signé. Quand la clé change, l'identité est
 * ré-émise : sans ça, un téléphone saisi dans le profil n'arriverait chez Intercom qu'au prochain
 * renouvellement, une heure plus tard.
 */
export function intercomProfileKey(profile: IntercomProfile | null | undefined): string {
  if (profile == null) return '';
  return [profile.firstName, profile.lastName, profile.phoneNumber]
    .map((value) => value ?? '')
    .join('\u0000');
}
